// SCHEDULE_SETUP — Section 10. Shown only when participant_state.schedule
// is null (first run, or after a researcher-driven ID reset).

import { useState } from 'react';
import { useParticipantStore } from '../store/participantStore';
import { requestNotificationPermission } from '../notifications/fcm';
import { registerPush } from '../worker/api';
import { formatTime, isSleepNextDay } from '../utils/schedule';

function TimeField({ label, hour, minute, onHourChange, onMinuteChange, note }) {
  return (
    <div className="card">
      <p className="text-dim text-sm mb-3">{label}</p>
      <div className="flex items-center gap-3">
        <select
          value={hour}
          onChange={(e) => onHourChange(Number(e.target.value))}
          className="min-h-touch flex-1 rounded-xl2 bg-raised border border-hairline px-3 text-lg"
        >
          {Array.from({ length: 24 }, (_, h) => (
            <option key={h} value={h}>
              {String(h).padStart(2, '0')}
            </option>
          ))}
        </select>
        <span className="text-xl text-dim">:</span>
        <select
          value={minute}
          onChange={(e) => onMinuteChange(Number(e.target.value))}
          className="min-h-touch flex-1 rounded-xl2 bg-raised border border-hairline px-3 text-lg"
        >
          {[0, 15, 30, 45].map((m) => (
            <option key={m} value={m}>
              {String(m).padStart(2, '0')}
            </option>
          ))}
        </select>
      </div>
      {note && <p className="text-amber text-xs mt-2">{note}</p>}
    </div>
  );
}

export default function ScheduleSetupScreen({ onDone }) {
  const patch = useParticipantStore((s) => s.patch);
  const participantId = useParticipantStore((s) => s.participant_id);
  const [wakeHour, setWakeHour] = useState(8);
  const [wakeMinute, setWakeMinute] = useState(0);
  const [sleepHour, setSleepHour] = useState(22);
  const [sleepMinute, setSleepMinute] = useState(0);
  const [busy, setBusy] = useState(false);

  const sleepIsNextDay = isSleepNextDay(wakeHour, wakeMinute, sleepHour, sleepMinute);

  async function finish(notificationsEnabled) {
    setBusy(true);
    const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    const schedule = {
      wake_hour: wakeHour,
      wake_minute: wakeMinute,
      sleep_hour: sleepHour,
      sleep_minute: sleepMinute,
      timezone,
    };

    let fcmToken = null;
    if (notificationsEnabled) {
      fcmToken = await requestNotificationPermission();
    }

    await patch({
      schedule,
      notifications_enabled: Boolean(fcmToken),
      fcm_token: fcmToken,
    });

    if (fcmToken) {
      try {
        await registerPush({ participantId, fcmToken, schedule });
      } catch (err) {
        console.error('register-push failed', err);
      }
    }

    setBusy(false);
    onDone();
  }

  return (
    <div className="screen">
      <h1 className="font-display text-2xl mb-1">Your daily schedule</h1>
      <p className="text-dim mb-6">
        We'll use this to space out your 5 sessions a day and send reminders.
      </p>

      <div className="flex flex-col gap-4 mb-6">
        <TimeField
          label="What time do you usually wake up?"
          hour={wakeHour}
          minute={wakeMinute}
          onHourChange={setWakeHour}
          onMinuteChange={setWakeMinute}
        />
        <TimeField
          label="What time do you usually go to sleep?"
          hour={sleepHour}
          minute={sleepMinute}
          onHourChange={setSleepHour}
          onMinuteChange={setSleepMinute}
          note={
            sleepIsNextDay
              ? `This means you sleep at ${formatTime(sleepHour, sleepMinute)} — the next day.`
              : null
          }
        />
      </div>

      <div className="mt-auto flex flex-col gap-3">
        <button type="button" disabled={busy} onClick={() => finish(true)} className="btn-primary">
          Allow notifications &amp; continue
        </button>
        <button type="button" disabled={busy} onClick={() => finish(false)} className="btn-secondary">
          Continue without notifications
        </button>
      </div>
    </div>
  );
}
